import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShieldCheck, LogOut, Search, Building2, Archive, TrendingUp, Send, GitMerge, Store, Users2, ShoppingBag, DollarSign, LifeBuoy, Settings as SettingsIcon, FileText, Sparkles, LineChart } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SUPER_ADMIN } from "@/constants/testIds";

export default function SuperAdminDashboardPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [overview, setOverview] = useState(null);
  const [audit, setAudit] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.get("/super-admin/overview"),
      api.get("/super-admin/audit", { params: { limit: 200 } }),
    ])
      .then(([o, a]) => {
        if (cancelled) return;
        setOverview(o.data);
        setAudit(a.data);
      })
      .catch((err) => {
        if (!cancelled) setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, []);

  // Server-side filter when the user types — keeps the table snappy at scale.
  useEffect(() => {
    if (!filter) return;
    const t = setTimeout(() => {
      api
        .get("/super-admin/audit", { params: { action: filter, limit: 200 } })
        .then((r) => setAudit(r.data))
        .catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [filter]);

  const handleLogout = async () => {
    await logout();
    navigate("/super-admin/login");
  };

  return (
    <div className="bg-cream min-h-screen" data-testid={SUPER_ADMIN.dashboard}>
      <header className="bg-[#0f1a14] text-[#F9F8F4] border-b border-[#1A422D]">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-6 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <ShieldCheck size={22} className="text-[#D9C68A]" />
            <div>
              <div className="overline" style={{ color: "#D9C68A" }}>Platform</div>
              <div className="font-serif text-2xl mt-0.5" data-testid="command-center-title">Command Center</div>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap justify-end">
            <div className="hidden sm:flex flex-col items-end leading-tight">
              <span className="overline" style={{ color: "#D9C68A" }}>Signed in</span>
              <span className="text-sm">{user?.email}</span>
            </div>
            <button
              type="button"
              onClick={handleLogout}
              data-testid="sa-signout"
              className="inline-flex items-center gap-2 border border-[#D9C68A]/50 text-[#D9C68A] px-3 py-1.5 text-xs uppercase tracking-[0.22em] hover:bg-[#1A422D] transition-colors"
            >
              <LogOut size={14} /> Sign Out
            </button>
          </div>
        </div>

        {/* Command Center module strip — Build Guide Phase 4 nav.
            Uses flex-wrap so all modules stay visible across breakpoints. */}
        <nav
          data-testid="command-center-nav"
          className="max-w-7xl mx-auto px-6 sm:px-10 pb-5 flex items-center gap-2 flex-wrap"
        >
          <CcNavLink to="/super-admin/clubs" testId="sa-nav-clubs" icon={<Building2 size={13} />}>Clubs</CcNavLink>
          <CcNavLink to="/super-admin/members" testId="sa-nav-members" icon={<Users2 size={13} />}>Members</CcNavLink>
          <CcNavLink to="/super-admin/vendors" testId="sa-nav-vendors" icon={<Store size={13} />}>Vendors</CcNavLink>
          <CcNavLink to="/super-admin/marketplace" testId="sa-nav-marketplace" icon={<ShoppingBag size={13} />}>Vendor Marketplace</CcNavLink>
          <CcNavLink to="/super-admin/revenue" testId="sa-nav-revenue" icon={<DollarSign size={13} />}>Revenue</CcNavLink>
          <CcNavLink to="/super-admin/demand" testId="sa-nav-analytics" icon={<TrendingUp size={13} />}>Analytics</CcNavLink>
          <CcNavLink to="/super-admin/join-analytics" testId="sa-nav-join-analytics" icon={<LineChart size={13} />}>Join Analytics</CcNavLink>
          <CcNavLink to="/super-admin/invitations" testId="sa-nav-invitations" icon={<Send size={13} />}>Invitations</CcNavLink>
          <CcNavLink to="/super-admin/merge" testId="sa-nav-merge" icon={<GitMerge size={13} />}>Merge &amp; Transfer</CcNavLink>
          <CcNavLink to="/super-admin/plans" testId="sa-nav-plans" icon={<Sparkles size={13} />}>Plans</CcNavLink>
          <CcNavLink to="/super-admin/support" testId="sa-nav-support" icon={<LifeBuoy size={13} />}>Support</CcNavLink>
          <CcNavLink to="/super-admin/settings" testId="sa-nav-settings" icon={<SettingsIcon size={13} />}>Settings</CcNavLink>
          <CcNavLink to="/super-admin/audit" testId="sa-nav-audit" icon={<FileText size={13} />}>Audit Logs</CcNavLink>
          <CcNavLink to="/super-admin/archive" testId="sa-nav-archive" icon={<Archive size={13} />}>Archive</CcNavLink>
        </nav>
      </header>

      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-10 space-y-8">
        {loading && <div className="overline text-[#7A7A7A]">Loading…</div>}
        {error && (
          <div className="px-4 py-3 text-sm border border-[#A33] bg-[#fbe9e9] text-[#8a1a1a]">{error}</div>
        )}

        {overview && (
          <>
            <div>
              <div className="overline">Platform Health</div>
              <h2 className="font-serif text-3xl text-[#1A1A1A] mt-1">Operational Snapshot</h2>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
              <Kpi label="Members" value={overview.members} testId={SUPER_ADMIN.kpiMembers}
                   hint={`${overview.onboarded_members} onboarded`} />
              <Kpi label="Active Orders" value={overview.active_orders} testId={SUPER_ADMIN.kpiActiveOrders} />
              <Kpi label="Trashed Orders" value={overview.trashed_orders} testId={SUPER_ADMIN.kpiTrashed}
                   hint="5-day retention" />
              <Kpi label="Wishlist Items" value={overview.wishlist_items} testId={SUPER_ADMIN.kpiWishlist} />
              <Kpi label="Audit Entries" value={overview.audit_log_entries} testId={SUPER_ADMIN.kpiAuditCount} />
            </div>
          </>
        )}

        <div>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="overline">Activity</div>
              <h2 className="font-serif text-3xl text-[#1A1A1A] mt-1">Audit Log</h2>
              <p className="text-sm text-[#4A4A4A] mt-2">
                Recent platform activity. Filter by action prefix (e.g. <code>order.</code>, <code>back_office.</code>, <code>super_admin.</code>).
              </p>
            </div>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9A9A9A]" />
              <input
                type="search"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                data-testid={SUPER_ADMIN.auditFilter}
                placeholder="Filter actions…"
                className="input-field pl-9 w-72"
              />
            </div>
          </div>
          <div
            className="mt-5 surface-card overflow-x-auto"
            data-testid={SUPER_ADMIN.auditTable}
          >
            <table className="w-full text-left">
              <thead className="bg-[#F3F1E9]">
                <tr className="text-xs uppercase tracking-wider text-[#4A4A4A]">
                  <th className="px-4 py-3">When</th>
                  <th className="px-4 py-3">Actor</th>
                  <th className="px-4 py-3">Action</th>
                  <th className="px-4 py-3">Target</th>
                  <th className="px-4 py-3">Detail</th>
                </tr>
              </thead>
              <tbody>
                {audit.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-10 text-center text-[#7A7A7A]">No entries yet.</td></tr>
                )}
                {audit.map((row) => (
                  <tr key={row.log_id} className="border-t border-[#E5E3D8]">
                    <td className="px-4 py-3 text-xs text-[#4A4A4A] whitespace-nowrap">
                      {new Date(row.at).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="text-[#1A1A1A]">{row.actor_name || row.actor_email || "—"}</div>
                      <div className="text-[10px] uppercase tracking-wider text-[#7A7A7A]">{row.actor_role}</div>
                    </td>
                    <td className="px-4 py-3 text-sm font-mono text-[#1A422D]">{row.action}</td>
                    <td className="px-4 py-3 text-xs text-[#4A4A4A]">
                      {row.target_type ? `${row.target_type}/${row.target_id || "—"}` : "—"}
                    </td>
                    <td className="px-4 py-3 text-xs text-[#4A4A4A]">
                      {row.meta && Object.keys(row.meta).length > 0 ? (
                        <code className="text-[10px] break-all">{JSON.stringify(row.meta)}</code>
                      ) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <FactoryResetPanel />
      </div>
    </div>
  );
}

function FactoryResetPanel() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const onClick = async () => {
    const typed = window.prompt(
      "FACTORY RESET — this nukes ALL members, ALL clubs, ALL employees, every order, every wishlist, every photo, " +
      "every audit log, and every forensic snapshot. The only things preserved are Platform Administrator accounts " +
      "and the master passcode.\n\nType FACTORY-RESET to confirm:",
    );
    if (typed === null) return;
    if (typed.trim() !== "FACTORY-RESET") {
      window.alert("Phrase doesn't match. Nothing was reset.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const { data } = await api.post("/super-admin/factory-reset", { confirm: "FACTORY-RESET" });
      window.alert(`Factory reset complete.\n\n${JSON.stringify(data.counts, null, 2)}\n\nReloading…`);
      window.location.reload();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid="sa-factory-reset-panel" className="mt-12 border-2 border-[#A33] bg-[#fff7f7] p-6 sm:p-7">
      <div className="flex items-start gap-3">
        <div className="flex-1">
          <div className="overline" style={{ color: "#8a1a1a" }}>Danger Zone</div>
          <h2 className="font-serif text-2xl mt-1 text-[#1A1A1A]">Factory Reset Platform</h2>
          <p className="text-sm text-[#4A4A4A] mt-2 leading-relaxed max-w-2xl">
            Hard-deletes every tenant document across every collection: clubs, members, employees, orders, wishlist,
            photos, audit log, and every forensic snapshot. Platform Administrator accounts and the back-office master
            passcode are preserved so you can sign back in afterward.
          </p>
          {error && (
            <div className="mt-3 px-3 py-2 text-xs border border-[#A33] bg-[#fbe9e9] text-[#8a1a1a]">{error}</div>
          )}
          <button
            type="button"
            onClick={onClick}
            disabled={busy}
            data-testid="sa-factory-reset-btn"
            className="mt-5 px-5 py-2.5 text-xs uppercase tracking-[0.18em] font-semibold bg-[#8a1a1a] hover:bg-[#A33] text-[#F9F8F4] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {busy ? "Wiping…" : "Factory Reset"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, hint, testId }) {
  return (
    <div data-testid={testId} className="surface-card p-5">
      <div className="overline">{label}</div>
      <div className="font-serif text-3xl mt-2 text-[#1A1A1A]">{value}</div>
      {hint && <div className="text-xs text-[#7A7A7A] mt-1">{hint}</div>}
    </div>
  );
}


function CcNavLink({ to, testId, icon, children }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className="inline-flex items-center gap-1.5 border border-[#D9C68A]/40 text-[#D9C68A] px-3 py-1.5 text-[11px] uppercase tracking-[0.22em] hover:bg-[#1A422D] hover:border-[#D9C68A] transition-colors"
    >
      {icon}
      {children}
    </Link>
  );
}
